import * as React from 'react';
import { Link } from 'react-router-dom';
import { LocalizedText } from 'bernie-l10n';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { UitkCardLink } from 'uitk-react-cards';
import { Viewport, ViewSmall, ViewLarge } from 'bernie-view';
import { FILTER_URL_PARAM_PREFIX } from '../../../../stores';
import { getFilteredObjectKeys } from '../../../../helpers/common-helper';
import { ActivityDestinationViewSearch } from '../../../../common/__generated__/api';
import { useStores } from '../../../../utils/hooks/useStores';
import { completeISUrl } from '../../../../helpers/is-link-helper';

export interface CardLinkProps {
  activity: ActivityDestinationViewSearch.ActivityInlineFragment;
  index: number;
  isRecommendationTile?: boolean;
  className?: string;
  endDate?: string;
  onClick?: (activity: ActivityDestinationViewSearch.ActivityInlineFragment, index: number) => void;
  startDate?: string;
  regionId?: string;
  regionName?: string;
}

export const CardLink: React.FC<CardLinkProps> = ({
  activity,
  onClick,
  index,
  isRecommendationTile,
  regionName,
  regionId
}) => {
  const { activityDetailStore, detailCriteriaStore, routeStore } = useStores();
  const startDateString = routeStore.queryParams.startDate;
  const endDateString = routeStore.queryParams.endDate;

  let searchLink = `?srp=true&location=${regionName}&rid=${regionId}&startDate=${startDateString}&endDate=${endDateString}`;

  if (!isRecommendationTile) {
    const filterQueryParams = getFilteredObjectKeys(routeStore.queryParams, FILTER_URL_PARAM_PREFIX);
    const slimSearchKeyword = routeStore.queryParams.slimSearchKeyword;
    const sort = routeStore.queryParams.sort;
    const selectedId = routeStore.queryParams.selectedId;

    filterQueryParams.forEach((filterParam: string) => {
      const filterVal = routeStore.queryParams[filterParam];
      if (filterVal) {
        searchLink = searchLink.concat(`&${filterParam}=${filterVal}`);
      }
    });
    if (slimSearchKeyword) {
      searchLink = searchLink.concat(`&slimSearchKeyword=${slimSearchKeyword}`);
    }
    if (sort) {
      searchLink = searchLink.concat(`&sort=${sort}`);
    }
    if (selectedId) {
      searchLink = searchLink.concat(`&selectedId=${selectedId}`);
    }
  }

  const swpApplied = routeStore.queryParams.swp !== 'off';
  const mctcValue = routeStore.queryParams.mctc;

  const resetInfositeForNewActivity = (newActivity: ActivityDestinationViewSearch.ActivityInlineFragment): void => {
    if (detailCriteriaStore.getActivityId() !== newActivity.id) {
      activityDetailStore.resetDetailsPage(newActivity);
    }
  };

  const handleRawClick = (): void => {
    if (onClick) {
      onClick(activity, index);
    }
  };

  const handleClick = (isMobile: boolean): void => {
    if (onClick) {
      detailCriteriaStore.setRecommendedActivity(isRecommendationTile);
      if (isMobile) {
        resetInfositeForNewActivity(activity);
      }
      onClick(activity, index);
    }
  };

  const isCardLinkView = (isMobile: boolean): JSX.Element => {
    const targetProp = isMobile
      ? null
      : {
          target: '_blank'
        };

    return (
      <Experiment name="LX_PWA_Client_Side_Rendering">
        <ExperimentControl>
          <UitkCardLink>
            <Link
              data-testid="card-link"
              onClick={(): void => handleClick(isMobile)}
              to={completeISUrl(activity, searchLink, swpApplied, index + 1, isRecommendationTile, mctcValue)}
              {...targetProp}
            >
              {activity.name}
              <span className="is-visually-hidden">
                <LocalizedText message="lx.search.card.infosite.link.accessibility.text" />
              </span>
            </Link>
          </UitkCardLink>
        </ExperimentControl>
        <ExperimentVariant bucket={1}>
          <UitkCardLink>
            <a
              data-testid="card-link"
              onClick={handleRawClick}
              href={completeISUrl(activity, searchLink, swpApplied, index + 1, isRecommendationTile, mctcValue)}
            >
              {activity.name}
              <span className="is-visually-hidden">
                <LocalizedText message="lx.search.card.infosite.link.accessibility.text" />
              </span>
            </a>
          </UitkCardLink>
        </ExperimentVariant>
      </Experiment>
    );
  };

  return (
    <Viewport>
      <ViewSmall>{isCardLinkView(true)}</ViewSmall>
      <ViewLarge>{isCardLinkView(false)}</ViewLarge>
    </Viewport>
  );
};
